import moment from 'moment';
import { AgentActivityLog } from '@trnsact/trnsact-shared-types';
import { DateFormats } from 'constants/common';
import { AssistantLogRow } from './types';

function extractTriggerDescription(trigger: Record<string, unknown> | null): string | null {
  if (!trigger) return null;
  if (trigger.email_from) return `${trigger.email_from} email`;
  if (trigger.user_uploaded_decision_letter)
    return `Decision letter uploaded by ${trigger.user_uploaded_decision_letter as string}`;
  return null;
}

interface PayloadAttachment {
  fileName: string;
  mimeType?: string;
  location?: { s3Key: string; s3Bucket: string } | null;
}

function extractAttachments(payload: Record<string, unknown> | null): { name: string | null; count: number } {
  if (!payload) return { name: null, count: 0 };
  const attachments = payload.attachments as PayloadAttachment[] | null;
  if (!Array.isArray(attachments) || attachments.length === 0) return { name: null, count: 0 };
  return { name: attachments[0].fileName ?? null, count: attachments.length };
}

export function extractMatchedRecord(
  associations: Record<string, unknown> | null
): { label: string; url: string | null } | null {
  if (!associations) return null;
  const voId = associations.vendor_opportunity_id as string | null | undefined;
  const csId = associations.credit_submission_id as string | null | undefined;
  const dynamicsCsId = associations.dynamics_credit_submission_id as string | null | undefined;
  if (dynamicsCsId) {
    const url = voId ? `/vo/${voId}?creditSubmissionId=${dynamicsCsId}#submissions` : null;
    return { label: `Credit Submission`, url };
  }
  if (voId) return { label: `Vendor Opportunity`, url: `/vo/${voId}` };
  return null;
}

export const mapAgentActivityToRow = (activity: AgentActivityLog): AssistantLogRow => ({
  id: activity.agentActivityLogId,
  createdDateTime: activity.createdDateTime
    ? moment(activity.createdDateTime).format(DateFormats.DateTimeShort12Hour)
    : null,
  processType: activity.processType,
  triggerDescription: extractTriggerDescription(activity.trigger as Record<string, unknown> | null),
  ...(() => {
    const { name, count } = extractAttachments(activity.payload as Record<string, unknown> | null);
    return { attachmentName: name, attachmentCount: count };
  })(),
  ...(() => {
    const matched = extractMatchedRecord(activity.associations as Record<string, unknown> | null);
    return { matchedRecord: matched?.label ?? null, matchedRecordUrl: matched?.url ?? null };
  })(),
  outcome: activity.outcome ?? null,
});
